/**
 * Utility functions for text manipulation
 */

/**
 * Strips HTML tags from a string and returns plain text.
 * Uses DOMParser for safe HTML parsing with regex fallback.
 * 
 * @param input - HTML string to strip
 * @returns Plain text without HTML tags
 */
export function stripHtmlToText(input: string | null | undefined): string {
  if (!input) return '';
  
  let text = '';
  
  try {
    // Use DOMParser for safe HTML parsing (browser environment)
    if (typeof DOMParser !== 'undefined') {
      const doc = new DOMParser().parseFromString(input, 'text/html');
      text = doc.body.textContent ?? '';
    } else {
      // Fallback: regex-based stripping for non-browser environments
      text = input.replace(/<[^>]*>/g, '');
    }
  } catch {
    // If parsing fails, use regex fallback
    text = input.replace(/<[^>]*>/g, '');
  }
  
  // Normalize whitespace: replace multiple spaces/newlines with single space
  text = text.replace(/\s+/g, ' ');
  
  // Trim leading/trailing whitespace
  return text.trim();
}

/**
 * Truncates text to a maximum length, adding ellipsis if truncated.
 * 
 * @param text - Text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text with ellipsis if needed
 */
export function truncateText(text: string, maxLength: number): string {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '…';
}

/**
 * Derives a plain-text preview from HTML/richtext content.
 * Strips tags, normalizes whitespace, truncates at word boundary.
 * 
 * @param html - HTML or richtext string
 * @param maxLen - Maximum character length (default 160)
 * @returns Plain text preview with ellipsis if truncated
 */
export function derivePreviewText(html: string | null | undefined, maxLen = 160): string {
  const plain = stripHtmlToText(html);
  if (!plain) return '';
  if (plain.length <= maxLen) return plain;
  // Truncate at word boundary
  const truncated = plain.slice(0, maxLen);
  const lastSpace = truncated.lastIndexOf(' ');
  return (lastSpace > maxLen * 0.6 ? truncated.slice(0, lastSpace) : truncated).trim() + '…';
}

/**
 * Returns true if `shortDesc` is a near-duplicate of the beginning of `fullHtml`.
 * Used to suppress rendering short_description as ingress when it would just
 * repeat the opening of the full description.
 */
export function isExcerptDuplicate(
  shortDesc: string | null | undefined,
  fullHtml: string | null | undefined,
): boolean {
  if (!shortDesc || !fullHtml) return false;
  const normShort = stripHtmlToText(shortDesc).toLowerCase().replace(/…$/, '').trim();
  const normFull = stripHtmlToText(fullHtml).toLowerCase();
  if (!normShort) return false;
  // If the short description is a prefix of (or equal to) the full text, it's a duplicate
  return normFull.startsWith(normShort);
}
